import {
    SiniestroItem,
    SiniestroStatus,
    VisitaRoamBadgeTone,
    VisitaRoamItem,
    VisitaRoamStatus
} from '../types/siniestros.types';
import { RoamSiniestroRaw, RoamVisitaRaw } from '../../../services/roamApi';
import { abbreviateOrganizationName } from '../../../constants/organizationLegends';
import { getDate } from '../../../utils/common';

const firstDefined = (source: Record<string, any>, keys: string[]) =>
    keys.find((key) => source?.[key] !== undefined && source?.[key] !== null);

const pickValue = (source: Record<string, any>, keys: string[]) => {
    const key = firstDefined(source, keys);
    return key ? source[key] : undefined;
};

const toStringValue = (value: unknown) => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const getPreventorName = (value: unknown) => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'object') {
        const record = value as Record<string, any>;
        const fullName = [record?.nombre, record?.apellido].filter(Boolean).join(' ').trim();

        if (fullName) {
            return fullName;
        }

        return toStringValue(record?.nombreCompleto || record?.name);
    }

    return toStringValue(value);
};

const getTipoSiniestroLabel = (value: unknown) => {
    const normalized = Number(value);

    if (normalized === 1) return 'Accidente de Trabajo';
    if (normalized === 3) return 'Accidente In Itinere';
    if (normalized === 6) return 'Enfermedad Profesional';

    return undefined;
};

const normalizeSiniestroStatus = (
    value: unknown
): { estado: SiniestroStatus; estadoLabel: string } => {
    const normalized = String(value ?? '')
        .trim()
        .toUpperCase();

    if (['1', 'PENDIENTE', 'PENDING'].includes(normalized)) {
        return { estado: 'PENDING', estadoLabel: 'Pendiente' };
    }

    if (['2', 'ABIERTO', 'OPEN'].includes(normalized)) {
        return { estado: 'OPEN', estadoLabel: 'Abierto' };
    }

    if (['3', 'FINALIZADO', 'FINALIZED', 'FINISHED'].includes(normalized)) {
        return { estado: 'FINALIZED', estadoLabel: 'Finalizado' };
    }

    if (['4', 'CANCELADO', 'CANCELLED'].includes(normalized)) {
        return { estado: 'CANCELLED', estadoLabel: 'Cancelado' };
    }

    return { estado: 'PENDING', estadoLabel: toStringValue(value) || 'Pendiente' };
};

const normalizeVisitaStatus = (
    value: unknown
): {
    estado: VisitaRoamStatus;
    estadoLabel: string;
} => {
    const normalized = String(value ?? '')
        .trim()
        .toUpperCase();

    if (['PROGRAMADA', 'PROGRAMMED'].includes(normalized)) {
        return { estado: 'PROGRAMMED', estadoLabel: 'Programada' };
    }

    if (['REALIZADA', 'DONE'].includes(normalized)) {
        return { estado: 'DONE', estadoLabel: 'Realizada' };
    }

    if (['7', 'SIN ASIGNAR', 'SIN_ASIGNAR', 'UNASSIGNED'].includes(normalized)) {
        return { estado: 'UNASSIGNED', estadoLabel: 'Sin Asignar' };
    }

    if (['CANCELADA', 'CANCELLED'].includes(normalized)) {
        return { estado: 'CANCELLED', estadoLabel: 'Cancelada' };
    }

    return {
        estado: 'PENDING',
        estadoLabel: toStringValue(value) || 'Pendiente'
    };
};

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getBadgeTone = (date: unknown): VisitaRoamBadgeTone => {
    const rawDate = toStringValue(date);

    if (!rawDate) {
        return 'normal';
    }

    const parsedDate = new Date(rawDate);

    if (Number.isNaN(parsedDate.getTime())) {
        return 'normal';
    }

    const today = getStartOfDay(new Date());
    const slaDate = getStartOfDay(parsedDate);
    const diffInDays = Math.floor((slaDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) {
        return 'expired';
    }

    if (diffInDays <= 2) {
        return 'urgent';
    }

    return 'normal';
};

const formatBadgeDate = (date: unknown) => {
    const rawDate = toStringValue(date);

    if (!rawDate) {
        return '';
    }

    const formattedDate = getDate(rawDate, 'DD/MM');

    if (!formattedDate) {
        return rawDate;
    }

    return formattedDate;
};

export const mapRoamVisita = (visita: RoamVisitaRaw): VisitaRoamItem => {
    const slaDate = pickValue(visita, [
        'fechaLimite',
        'fechaVencimiento',
        'vencimiento',
        'fechaProgramada'
    ]);
    const status = normalizeVisitaStatus(
        pickValue(visita, [
            'estado',
            'estadoLabel',
            'nombreEstado',
            'descripcionEstado',
            'idEstado'
        ])
    );

    return {
        id: toStringValue(pickValue(visita, ['id', 'idVisita'])) || '',
        nro: Number(pickValue(visita, ['nro', 'nroVisita', 'numero', 'orden'])) || 0,
        estadoId: Number(pickValue(visita, ['idEstado'])) || undefined,
        estado: status.estado,
        estadoLabel: status.estadoLabel,
        preventor:
            getPreventorName(
                pickValue(visita, [
                    'nombrePreventor',
                    'preventor',
                    'preventorNombre',
                    'nombreUsuario'
                ])
            ) || null,
        localidad: toStringValue(
            pickValue(visita, ['localidad', 'nombreLocalidad', 'ciudad', 'nombreCiudad'])
        ),
        fechaVencimiento: toStringValue(slaDate) || '',
        fechaRealizada:
            toStringValue(
                pickValue(visita, [
                    'fechaRealizada',
                    'fechaVisita',
                    'fechaCierre',
                    'fechaEjecucion'
                ])
            ) || undefined,
        badgeLabel: formatBadgeDate(slaDate),
        badgeTone: getBadgeTone(slaDate),
        motivoFalla: visita?.motivoFalla ?? null
    };
};

export const mapRoamSiniestro = (siniestro: RoamSiniestroRaw): SiniestroItem => {
    const status = normalizeSiniestroStatus(
        pickValue(siniestro, [
            'estado',
            'estadoLabel',
            'nombreEstado',
            'descripcionEstado',
            'idEstado'
        ])
    );

    const accidentado = siniestro?.accidentado || {};
    const empleador = siniestro?.empleador || {};
    const lugarTrabajo = siniestro?.lugarTrabajo || {};
    const visitasRaw = (pickValue(siniestro, ['visitasRoam', 'visitas']) as RoamVisitaRaw[]) || [];

    const empleadoNombre =
        [toStringValue(accidentado?.nombre), toStringValue(accidentado?.apellido)]
            .filter(Boolean)
            .join(' ')
            .trim() ||
        [
            toStringValue(pickValue(siniestro, ['nombreEmpleado', 'empleadoNombre'])),
            toStringValue(pickValue(siniestro, ['apellidoEmpleado', 'empleadoApellido']))
        ]
            .filter(Boolean)
            .join(' ')
            .trim() ||
        '-';

    return {
        id: toStringValue(pickValue(siniestro, ['id'])) || '',
        nroSiniestro: toStringValue(pickValue(siniestro, ['nroSiniestro'])) || '',
        nroProvisorio: toStringValue(pickValue(siniestro, ['nroProvisorio'])),
        nroRoam: toStringValue(pickValue(siniestro, ['nroRoam'])),
        cliente: abbreviateOrganizationName(
            toStringValue(siniestro?.organismo?.organismoCentral?.organizacion?.nombre) ||
                toStringValue(
                    siniestro?.establecimiento?.organismo?.organismoCentral?.organizacion?.nombre
                ) ||
                toStringValue(
                    pickValue(siniestro, [
                        'cliente',
                        'nombreCliente',
                        'organizacion',
                        'nombreOrganizacion'
                    ])
                )
        ),
        clienteKey: toStringValue(
            pickValue(siniestro, ['clienteKey', 'abreviaturaCliente', 'siglaCliente'])
        ),
        empresa: abbreviateOrganizationName(
            toStringValue(empleador?.nombre) ||
                toStringValue(
                    pickValue(siniestro, ['empresa', 'nombreEmpresa', 'nombreEstablecimiento'])
                ) ||
                toStringValue(lugarTrabajo?.nombre)
        ),
        cuitEmpresa:
            toStringValue(empleador?.cuit) ||
            toStringValue(pickValue(siniestro, ['cuitEmpresa', 'cuit'])),
        provincia:
            toStringValue(lugarTrabajo?.provincia) ||
            toStringValue(pickValue(siniestro, ['provincia', 'nombreProvincia'])),
        idEstablecimiento:
            Number(
                lugarTrabajo?.idEstablecimiento ||
                    lugarTrabajo?.id ||
                    pickValue(siniestro, ['idEstablecimiento'])
            ) || null,
        establecimientoNombre:
            toStringValue(lugarTrabajo?.nombre) ||
            toStringValue(pickValue(siniestro, ['nombreEstablecimiento'])),
        empleado: {
            nombre: empleadoNombre,
            cuil:
                toStringValue(accidentado?.cuil) ||
                toStringValue(pickValue(siniestro, ['cuil', 'cuilEmpleado'])),
            dni:
                toStringValue(accidentado?.documento) ||
                toStringValue(pickValue(siniestro, ['dni', 'dniEmpleado'])),
            edad: undefined,
            sexo: toStringValue(pickValue(siniestro, ['sexo']))
        },
        nroContrato: toStringValue(siniestro?.organismo?.organismoCentral?.contrato) || undefined,
        cuitContrato: toStringValue(siniestro?.organismo?.organismoCentral?.cuit) || undefined,
        nombreContrato: toStringValue(siniestro?.organismo?.organismoCentral?.nombre) || undefined,
        nombreOrganismo: toStringValue(siniestro?.organismo?.nombre) || undefined,
        cuitOrganismo: toStringValue(siniestro?.organismo?.cuit) || undefined,
        tipo:
            getTipoSiniestroLabel(siniestro?.idTipoSiniestro) ||
            toStringValue(
                pickValue(siniestro, [
                    'tipoSiniestro',
                    'nombreTipoSiniestro',
                    'descripcionTipoSiniestro'
                ])
            ),
        estado: status.estado,
        estadoLabel: status.estadoLabel,
        estadoSRT: toStringValue(
            pickValue(siniestro, [
                'estadoSRT',
                'estadoSrt',
                'nombreEstadoSRT',
                'descripcionEstadoSRT'
            ])
        ),
        estadoSrt: (() => {
            const raw = pickValue(siniestro, ['estadoSrt', 'estado_srt']);
            const num = Number(raw);
            return Number.isFinite(num) && num > 0 ? num : undefined;
        })(),
        idAccidenteLaboral: (() => {
            const id = Number(siniestro?.accidenteLaboral?.id);
            return Number.isFinite(id) && id > 0 ? id : undefined;
        })(),
        fechaVencimientoSRT: toStringValue(
            pickValue(siniestro, ['fechaVencimientoSRT'])
        ),
        fechaPresentacionVisita: toStringValue(
            pickValue(siniestro, ['fechaPresentacionVisita'])
        ),
        ocurrencia: toStringValue(
            pickValue(siniestro, ['ocurrencia', 'fechaOcurrrencia', 'fechaOcurrencia'])
        ),
        relato: toStringValue(pickValue(siniestro, ['relatoHecho', 'relato', 'descripcion'])),
        // Sin el flag, tieneLugarOcurrenciaCompleto calcula la regla en el Front (SMP-585)
        lugarOcurrenciaCompleto:
            typeof siniestro?.lugarOcurrenciaCompleto === 'boolean'
                ? siniestro.lugarOcurrenciaCompleto
                : undefined,
        lugarOcurrencia: siniestro?.lugarOcurrencia
            ? {
                  calle: toStringValue(siniestro.lugarOcurrencia?.calle),
                  altura: toStringValue(siniestro.lugarOcurrencia?.altura),
                  piso: toStringValue(siniestro.lugarOcurrencia?.piso) || null,
                  depto:
                      toStringValue(siniestro.lugarOcurrencia?.depto) ||
                      toStringValue(siniestro.lugarOcurrencia?.departamento) ||
                      null,
                  codigoPostal: toStringValue(siniestro.lugarOcurrencia?.codigoPostal),
                  idLocalidad: Number(siniestro.lugarOcurrencia?.idLocalidad) || null,
                  localidad: siniestro.lugarOcurrencia?.localidad
                      ? {
                            id: Number(siniestro.lugarOcurrencia.localidad?.id) || null,
                            nombre: toStringValue(siniestro.lugarOcurrencia.localidad?.nombre),
                            provincia: siniestro.lugarOcurrencia.localidad?.provincia
                                ? {
                                      nombre: toStringValue(
                                          siniestro.lugarOcurrencia.localidad?.provincia?.nombre
                                      )
                                  }
                                : undefined
                        }
                      : undefined
              }
            : undefined,
        establecimiento: siniestro?.establecimiento
            ? {
                  direccion: siniestro.establecimiento?.direccion
                      ? {
                            calle:
                                toStringValue(siniestro.establecimiento.direccion?.calle) ||
                                undefined,
                            altura:
                                toStringValue(siniestro.establecimiento.direccion?.altura) ||
                                undefined,
                            piso:
                                toStringValue(siniestro.establecimiento.direccion?.piso) || null,
                            depto:
                                toStringValue(
                                    siniestro.establecimiento.direccion?.departamento ||
                                        siniestro.establecimiento.direccion?.depto
                                ) || null,
                            codigoPostal: siniestro.establecimiento.direccion?.localidad
                                ?.codigoPostal
                                ? String(
                                      siniestro.establecimiento.direccion.localidad.codigoPostal
                                  )
                                : undefined,
                            localidad: siniestro.establecimiento.direccion?.localidad
                                ? {
                                      id:
                                          Number(
                                              siniestro.establecimiento.direccion?.localidad?.id
                                          ) || null,
                                      nombre: toStringValue(
                                          siniestro.establecimiento.direccion?.localidad?.nombre
                                      ),
                                      codigoPostal: siniestro.establecimiento.direccion?.localidad
                                          ?.codigoPostal
                                          ? Number(
                                                siniestro.establecimiento.direccion.localidad
                                                    .codigoPostal
                                            )
                                          : undefined,
                                      provincia: siniestro.establecimiento.direccion?.localidad
                                          ?.provincia
                                          ? {
                                                id:
                                                    Number(
                                                        siniestro.establecimiento.direccion
                                                            ?.localidad?.provincia?.id
                                                    ) || null,
                                                nombre: toStringValue(
                                                    siniestro.establecimiento.direccion?.localidad
                                                        ?.provincia?.nombre
                                                )
                                            }
                                          : undefined
                                  }
                                : undefined
                        }
                      : undefined
              }
            : undefined,
        preventorPrincipal:
            toStringValue(
                pickValue(siniestro, ['nombrePreventor', 'preventorPrincipal', 'preventor'])
            ) || null,
        visitasRoam: Array.isArray(visitasRaw) ? visitasRaw.map(mapRoamVisita) : [],
        visitasExtra:
            Number(
                pickValue(siniestro, ['visitasExtra', 'cantidadVisitasExtra', 'cantidadVisitas'])
            ) || undefined,
        idTipoSiniestro: Number(siniestro?.idTipoSiniestro) || null,
        fechaDenuncia: toStringValue(pickValue(siniestro, ['fechaDenuncia'])) || null,
        fechaAltaRoam: toStringValue(pickValue(siniestro, ['fechaAltaRoam'])) || null,
        patologiasTrazadoras:
            toStringValue(
                siniestro?.datosMedicos?.patologiasTrazadoras ||
                    pickValue(siniestro, [
                        'patologiasTrazadoras',
                        'patologiaTrazadora',
                        'patologiaAtrazadora',
                        'patologiaAtrasadora'
                    ])
            ) || null
    };
};
